import { EndCallControl } from '../components/EndCallControl.js';
import { ConfirmDialog } from '../components/ui.js';
import { CallFeedback } from '../components/CallFeedback.js';
import { retryFeedback, submitFeedback, signalSubmissionId } from '../lib/callFeedback.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router';
import { Room, RoomEvent, Participant, Track } from 'livekit-client';
import type { Room as RoomInstance } from 'livekit-client';
import { api, type RoomInviteResponse } from '../lib/api.js';
import { Markdown } from '../components/Markdown.js';
import { Avatar } from '../components/Avatar.js';
import { MeetingAppearance, useMeetingAppearance } from '../components/AppearancePicker.js';
import { avatarUrl } from '../lib/appearance.js';
import { BOT_IDENTITY, BOT_NAME, type User, RoomPanel } from './RoomPanel.js';
import { useMeeting } from '../state/meeting.js';
import { jabberrStatus, type JabberrAttributes, isJabberrInRoom } from '../state/jabberrStatus.js';
import { useSession } from '../state/session.js';
import { isHumanMeetingSegment } from '@jabberr/core';
import { TalkModeControl } from '../components/TalkModeControl.js';
import { talkModeHint } from '../lib/plain.js';
import { CaptureProfileControl } from '../components/CaptureProfileControl.js';
import { captureSettings, readCaptureProfile, saveCaptureProfile, voiceCaptureOptions, type CaptureProfile } from '../lib/voiceCapture.js';
import { ProfessionalReceipt } from '../components/ProfessionalReceipt.js';
import { MoreIcon } from '../components/icons.js';
import { CallSettings, SettingsSection } from '../components/CallSettings.js';
import { ProfessionalControl } from '../components/ProfessionalControl.js';
import { requestCallAssistant } from '../lib/callEntry.js';
import { DuplexStatus } from '../components/DuplexStatus.js';
import { duplexSignals, professionalDuplexActive, type DuplexSignals } from '../state/duplexSignals.js';
import { TranscriptGroups } from '../components/TranscriptGroups.js';
import { JabberrTile, MeetingHeader, PersonTile, StatusIndicator } from '../components/RoomTiles.js';
import { canInviteFromSegments, formatSegmentTime, joinFailure, leaveChoice } from '../lib/room.js';
import { useFollowScroll } from '../lib/useFollowScroll.js';

interface JoinResponse {
  url: string;
  token: string;
}

const POLL_MS = 15_000;


export function RoomPage() {
  const [confirmLeave, setConfirmLeave] = useState<string | null>(null);
  const [sharedInputCapability, setSharedInputCapability] = useState<{ meetingId: string; allowed: boolean } | null>(null);
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const entry = useLocation().state as { inviteJabberr?: boolean } | null;
  const [assistantJoinError, setAssistantJoinError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);
  const [confirmEndCall, setConfirmEndCall] = useState<string | null>(null);
  const { user } = useSession();
  const [state, setState] = useState<'idle' | 'connecting' | 'connected' | 'unavailable'>('idle');
  const [failure, setFailure] = useState<'unavailable' | 'mic-denied' | 'ended' | 'stale' | null>(null);
  const [peers, setPeers] = useState<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [captureProfile, setCaptureProfile] = useState<CaptureProfile>(readCaptureProfile);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const [effectiveCapture, setEffectiveCapture] = useState('');
  const [panelUsers, setPanelUsers] = useState<User[]>([]);
  const [selfPresence, setSelfPresence] = useState<'online' | 'offline' | 'unavailable'>('online');
  const [speakingIds, setSpeakingIds] = useState<Set<string>>(new Set());
  const [invites, setInvites] = useState<{ meetingId: string; title: string; invitedBy: string }[]>([]);
  const [listenMode, setListenMode] = useState<'always' | 'mentions' | 'keen' | 'silent' | null>(null);
  const [listenBusy, setListenBusy] = useState(false);
  const roomRef = useRef<RoomInstance | null>(null);
  const [jabberrAttributes, setJabberrAttributes] = useState<JabberrAttributes>(undefined);
  const [jabberrInRoom, setJabberrInRoom] = useState(false);
  // Optional panels start closed so the call stays in focus.
  const [showPeople, setShowPeople] = useState(false);
  // Opening a panel remains an explicit choice at every viewport.
  const [showTranscript, setShowTranscript] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const audioHostRef = useRef<HTMLDivElement | null>(null);

  // Use the meeting hook for the transcript.
  const { meeting, project, canEnd, segments, loading: meetingLoading, loadError: meetingError, ended: endedRaw, updateListenMode: meetingUpdateListenMode, updateTitle, endMeeting: meetingEnd } = useMeeting(meetingId || '');
  const ended = endedRaw ?? false;
  // The meeting's shared assistant artwork; everyone in the room sees the same one.
  const appearance = useMeetingAppearance(meetingId || '', meeting?.jabberrAssistantImageId ?? meeting?.jabberrAvatarId);
  const [leftCall, setLeftCall] = useState(false);
  const [professionalActive, setProfessionalActive] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    const retry = () => { void retryFeedback(user.id); };
    retry(); window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [user?.id]);

  const [duplex, setDuplex] = useState<DuplexSignals | null>(null);
  const professionalLive = professionalDuplexActive(professionalActive, jabberrAttributes?.['lk.agent.state']) && state === 'connected' && !ended && jabberrInRoom;
  useEffect(() => {
    const room = roomRef.current;
    if (!professionalLive || !room) { setDuplex(null); return; }
    const refresh = () => {
      const next = duplexSignals(true, muted, room.localParticipant, room.remoteParticipants.get(BOT_IDENTITY));
      setDuplex(previous => previous?.peopleSpeaking === next?.peopleSpeaking && previous?.jabberrSpeaking === next?.jabberrSpeaking ? previous : next);
    };
    refresh();
    room.on(RoomEvent.ActiveSpeakersChanged, refresh);
    // Levels change even when the active-speaker list stays the same.
    const timer = window.setInterval(refresh, 100);
    return () => { window.clearInterval(timer); room.off(RoomEvent.ActiveSpeakersChanged, refresh); };
  }, [professionalLive, muted, meetingId]);
  // Gate immediately on session/connection changes, before the effect clears its snapshot.
  const liveDuplex = professionalLive ? duplex : null;

  // Participants map for the speaking grid: key by peerKeys, includes self.
  const [participants, setParticipants] = useState<Record<string, { identity: string; name?: string; muted?: boolean }>>({});

  // Build participants map from remote participants.
  const refreshParticipants = useCallback((room: RoomInstance) => {
    const map: Record<string, { identity: string; name?: string; muted?: boolean }> = {};
    for (const rp of room.remoteParticipants.values()) {
      const key = rp.identity === BOT_IDENTITY ? BOT_IDENTITY : (rp.name || rp.identity);
      map[key] = { identity: rp.identity, name: rp.name || undefined, muted: rp.identity === BOT_IDENTITY ? false : !rp.isMicrophoneEnabled };
    }
    // Add local participant as "you".
    if (room.localParticipant) {
      const selfKey = user?.name || user?.id || 'You';
      if (!map[selfKey]) {
        map[selfKey] = { identity: user?.id || '', name: user?.name || undefined };
      }
    }
    setPeers((prev) => {
      const next = Object.values(map).map((p) => (p.identity === BOT_IDENTITY ? BOT_IDENTITY : (p.name || p.identity)));
      return next;
    });
    setParticipants(map);
  }, [user]);

  const getBotAttributes = (room: RoomInstance): JabberrAttributes => {
    const bot = room.remoteParticipants.get(BOT_IDENTITY);
    return (bot?.attributes as JabberrAttributes) || undefined;
  };

  // Presence roster: on mount and every 15s while on the room page.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { users } = await api.presence.users();
        if (alive) setPanelUsers(users.map((u) => ({ ...u, inRoom: false })));
        if (alive && meetingId) {
          try {
            const { meeting: m } = await api.meetings.get(meetingId);
            if (alive) setListenMode(m.listenMode);
          } catch {
            // ignore
          }
        }
      } catch {
        // best-effort
      }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [meetingId]);

  // Incoming meeting invites.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { invites: list } = await api.presence.invites();
        if (alive) setInvites(list.filter((i) => (i.meetingId || i.conversationId) !== meetingId).map((i) => ({
          meetingId: i.meetingId || '',
          title: i.title || '',
          invitedBy: i.invitedBy,
        })) as { meetingId: string; title: string; invitedBy: string }[]);
      } catch {
        // ignore
      }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [meetingId]);

  const handleInvite = useCallback(
    (target: User) => {
      if (!meetingId) return;
      void api.meetings.invitePerson(meetingId, target.id).catch(() => {});
    },
    [meetingId],
  );

  const handleStatusChange = useCallback((status: 'available' | 'unavailable') => {
    const next = status === 'available' ? 'online' : 'unavailable';
    setSelfPresence(next);
    void api.presence.setStatus(next).catch(() => {});
  }, []);

  const dismissInvite = useCallback((invite: { meetingId: string }) => {
    setInvites((prev) => prev.filter((i) => i.meetingId !== invite.meetingId));
    void api.presence.clearInvite(invite.meetingId).catch(() => {});
  }, []);

  const joinInvite = useCallback(
    (invite: { meetingId: string }) => {
      setInvites((prev) => prev.filter((i) => i.meetingId !== invite.meetingId));
      void api.presence.clearInvite(invite.meetingId).catch(() => {});
      navigate(`/meet/${invite.meetingId}`);
    },
    [navigate],
  );

  // Check Jabberr attributes whenever they change.
  const handleJabberrAttributes = useCallback((attrs: JabberrAttributes) => {
    setJabberrAttributes(attrs);
  }, []);

  // Cleanup audio elements on leave/unmount.
  useEffect(() => {
    return () => {
      const host = audioHostRef.current;
      if (host) {
        while (host.firstChild) {
          host.removeChild(host.firstChild);
        }
      }
    };
  }, []);

  // Audio event handlers attached to the room.
  const attachAudio = useCallback((room: RoomInstance) => {
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.dataset.roomAudio = '1';
        audioHostRef.current?.appendChild(el);
      }
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => el.remove());
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      setAudioBlocked(!room.canPlaybackAudio);
    });
  }, []);

  // The transcript follows the conversation unless the reader has scrolled up; then a pill offers the way back.
  const { ref: transcriptRef, showNewPill, jumpToBottom, onScroll: handleTranscriptScroll } = useFollowScroll(segments.length, [showTranscript, state]);

  const handleStartAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.startAudio();
    } catch {
      // Ignore failures; the AudioPlaybackStatusChanged handler will update state.
    }
  }, []);

  const connectToRoom = useCallback(async () => {
    if (!meetingId) return;
    setState('connecting');
    setFailure(null);
    try {
      const { url, token } = (await api.meetings.token(meetingId)) as JoinResponse;
      const room = new Room({ audioCaptureDefaults: voiceCaptureOptions(captureProfile) });
      roomRef.current = room;

      // Attach remote audio tracks before connecting.
      attachAudio(room);

      room.on(RoomEvent.ConnectionStateChanged, (s) => {
        if (s === 'connected') {
          setState('connected');
          refreshParticipants(room);
          // Check initial Jabberr attributes.
          const attrs = getBotAttributes(room);
          handleJabberrAttributes(attrs);
          setJabberrInRoom(isJabberrInRoom(Object.fromEntries(room.remoteParticipants.entries()), BOT_IDENTITY));
        } else if (s === 'disconnected') {
          setState('idle');
          setAudioBlocked(false);
        }
      });

      room.on(RoomEvent.ParticipantConnected, () => {
        refreshParticipants(room);
        const attrs = getBotAttributes(room);
        handleJabberrAttributes(attrs);
        setJabberrInRoom(isJabberrInRoom(Object.fromEntries(room.remoteParticipants.entries()), BOT_IDENTITY));
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        refreshParticipants(room);
        const bot = room.remoteParticipants.get(BOT_IDENTITY);
        handleJabberrAttributes(bot?.attributes as JabberrAttributes);
        setJabberrInRoom(isJabberrInRoom(Object.fromEntries(room.remoteParticipants.entries()), BOT_IDENTITY));
      });

      // Track active speakers for the speaking indicator.
      // Identity and name both go in, so every tile matches whichever key it was built from, yours included.
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        setSpeakingIds(new Set(speakers.flatMap((s) => (s.name ? [s.identity, s.name] : [s.identity]))));
      });
      // Someone else's mute shows on their tile, not only your own.
      for (const event of [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted, RoomEvent.TrackPublished, RoomEvent.TrackUnpublished] as const) {
        room.on(event, () => refreshParticipants(room));
      }

      // Track Jabberr attribute changes.
      room.on(RoomEvent.ParticipantAttributesChanged, (changedAttrs: Record<string, string>, participant: Participant) => {
        if (participant.identity === BOT_IDENTITY) {
          handleJabberrAttributes(participant.attributes as JabberrAttributes);
        }
      });

      await room.connect(url, token);
      const microphone = await room.localParticipant.setMicrophoneEnabled(true, voiceCaptureOptions(captureProfile));
      if (microphone?.audioTrack) setEffectiveCapture(captureSettings(microphone.audioTrack.mediaStreamTrack));
      // User gesture: start audio playback (browser autoplay policy).
      try {
        await room.startAudio();
      } catch {
        // Ignore; AudioPlaybackStatusChanged will update state.
      }
      setAudioBlocked(!room.canPlaybackAudio);
      refreshParticipants(room);
      handleJabberrAttributes(getBotAttributes(room));
      setJabberrInRoom(isJabberrInRoom(Object.fromEntries(room.remoteParticipants.entries()), BOT_IDENTITY));
      setState('connected');
      setAssistantJoinError(await requestCallAssistant(meetingId, entry?.inviteJabberr === true, room.remoteParticipants.has(BOT_IDENTITY)));
    } catch (e) {
      roomRef.current?.disconnect();
      roomRef.current = null;
      setFailure(joinFailure(e));
      setState('unavailable');
    }
  }, [meetingId, captureProfile, refreshParticipants, handleJabberrAttributes, attachAudio, entry?.inviteJabberr]);

  const changeCaptureProfile = async (profile: CaptureProfile) => {
    setCaptureBusy(true); setCaptureError('');
    try {
      const track = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack;
      if (track) {
        await track.restartTrack(voiceCaptureOptions(profile));
        setEffectiveCapture(captureSettings(track.mediaStreamTrack));
      }
      setCaptureProfile(profile); saveCaptureProfile(profile);
    } catch { setCaptureError('Could not change microphone arrangement. Reconnect and try again.'); }
    finally { setCaptureBusy(false); }
  };

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const newMuted = !muted;
    setMuted(newMuted);
    await room.localParticipant.setMicrophoneEnabled(!newMuted);
  }, [muted]);

  const leaveRoom = useCallback(async () => {
    const room = roomRef.current;
    if (room) {
      try { await room.startAudio(); } catch { /* ignore */ }
      await room.disconnect();
    }
    roomRef.current = null;
    setState('idle');
    setAudioBlocked(false);
    setLeftCall(true);
  }, []);

  const reconnect = useCallback(async () => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) { try { await room.disconnect(); } catch { /* reconnecting anyway */ } }
    await connectToRoom();
  }, [connectToRoom]);

  const leave = useCallback(async () => {
    const humans = Object.values(participants).filter((p) => p.identity !== BOT_IDENTITY).length;
    if (meetingId && leaveChoice(humans, canEnd) === 'ask') {
      setConfirmLeave(meetingId);
      return;
    }
    await leaveRoom();
  }, [participants, meetingId, canEnd, leaveRoom]);

  // Jabberr status from attributes.
  const [participationBusy, setParticipationBusy] = useState(false);
  const [participationMessage, setParticipationMessage] = useState('');
  const [rated, setRated] = useState<Record<string, string>>({});
  const controlsReady = jabberrInRoom && jabberrAttributes?.['jabberr.controls'] === '1';
  const canAnswerSharedInput = state === 'connected' && !ended && sharedInputCapability?.meetingId === meetingId && sharedInputCapability?.allowed === true;
  useEffect(() => {
    setSharedInputCapability(null);
    if (!meetingId || !controlsReady || ended || state !== 'connected') return;
    let alive = true;
    let lastId: string | undefined;
    const refresh = async () => {
      try {
        const { command, canAnswerSharedInput: allowed } = await api.meetings.participation(meetingId);
        if (alive) setSharedInputCapability({ meetingId, allowed: allowed === true });
        if (alive && command && command.id !== lastId) {
          lastId = command.id;
          setParticipationMessage(`${command.actorName} ${command.action === 'answer' ? 'invited Jabberr to answer' : 'asked Jabberr to stop this reply'}.`);
        }
      } catch { if (alive) setSharedInputCapability(null); /* Button requests surface actionable errors. */ }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 1500);
    return () => { alive = false; window.clearInterval(timer); };
  }, [meetingId, controlsReady, ended, state]);
  const signalParticipation = async (action: 'answer' | 'stop') => {
    if (!meetingId) return;
    setParticipationBusy(true);
    try {
      await api.meetings.signal(meetingId, action);
      setParticipationMessage(action === 'answer' ? 'Jabberr has been invited to answer at the next opening.' : 'Stop requested.');
    } catch (error) { setParticipationMessage(error instanceof Error ? error.message : 'Could not send the request.'); }
    finally { setParticipationBusy(false); }
  };
  const rateContribution = async (responseId: string, rating: 'useful' | 'not_needed' | 'bad_timing') => {
    if (!meetingId) return;
    try {
      if (!user?.id) return;
      const saved = await submitFeedback(user.id, meetingId, { kind: 'contribution', submissionId: await signalSubmissionId(user.id, meetingId, `${responseId}:${rating}`), observedAt: new Date().toISOString(), responseId, rating }, `${responseId}:${rating}`);

      setRated((previous) => ({ ...previous, [responseId]: rating }));
      setParticipationMessage(saved ? 'Feedback saved to the corpus.' : 'Feedback saved on this device for retry.');
    } catch { setParticipationMessage('Feedback could not be saved. Please try again.'); }
  };
  const status = jabberrStatus(jabberrAttributes, participants, BOT_IDENTITY);

  // Determine who is in room from panel users (data-driven, not bot state).
  const botInRoom = panelUsers.some(u => u.id === BOT_IDENTITY || u.name === BOT_IDENTITY);
  // If Jabberr is in the room per status bar, also check room participants.
  const effectiveBotInRoom = jabberrInRoom || botInRoom;

  // Reflect the server mode without sending a second control mutation.
  useEffect(() => {
    if (meeting) {
      setListenMode(meeting.listenMode);
    }
  }, [meeting?.listenMode]);

  // An ended meeting hangs up: the microphone stops and nobody keeps hearing the others. Before,
  // the page only showed "Ended" and the call carried on underneath (2026-09-23).
  useEffect(() => {
    if (!ended) return;
    const room = roomRef.current;
    roomRef.current = null;
    if (room) void room.disconnect();
  }, [ended]);

  // Leaving the page leaves the call too; otherwise the connection, and the microphone, outlive the page.
  useEffect(() => () => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) void room.disconnect();
  }, []);

  const handleEnd = useCallback(async (leaveAfter = false) => {
    setEndError(null);
    try {
      await meetingEnd();
      if (leaveAfter) await leaveRoom();
    } catch (err) { setEndError(err instanceof Error ? err.message : 'The call could not be ended. Try again.'); }
  }, [meetingEnd, leaveRoom]);


  if (leftCall || ended || meeting?.status === 'ended') {
    return (
      <div className="page room-page">
        <div className="room-center room-card">
          <h2 className="room-title">{leftCall && !ended ? 'You left the call' : 'Call ended'}</h2>
          <p className="room-text">{meeting?.title ?? 'Meeting'}</p>
          {meeting?.recordingId ? (
            <Link to={`/r/${meeting.recordingId}`} className="btn btn-primary">Open the transcript</Link>
          ) : null}
          <button type="button" className="link-btn" onClick={() => navigate(meeting ? `/p/${meeting.projectId}` : '/')}>
            Back to the project
          </button>
          <EndCallControl key={meetingId} canEnd={canEnd} status={meeting?.status} onEnd={meetingEnd} />
          {user?.id && meetingId && <ProfessionalReceipt meetingId={meetingId} userId={user.id} />}
          {user?.id && meetingId && <CallFeedback key={`${user.id}:${meetingId}`} ownerId={user.id} meetingId={meetingId} />}
        </div>
      </div>
    );
  }

  if (state === 'idle') {
    return (
      <div className="page room-page">
        <div className="room-center room-card">
          <img className="room-prejoin-avatar" src={avatarUrl(appearance.avatarId)} alt="" aria-hidden="true" />
          <h2 className="room-title">Meeting</h2>
          <p className="room-text">Join the meeting with your microphone.</p>
          <CaptureProfileControl value={captureProfile} busy={captureBusy} onChange={(value) => void changeCaptureProfile(value)} />
          {canEnd && meeting?.status === 'live' ? <MeetingAppearance appearance={appearance} inline /> : null}
          <button type="button" className="btn btn-primary" onClick={() => void connectToRoom()}>
            Join the meeting
          </button>
          <button type="button" className="link-btn" onClick={() => navigate('/')}>
            Go home
          </button>
          <EndCallControl key={meetingId} canEnd={canEnd} status={meeting?.status} onEnd={meetingEnd} />
        </div>
      </div>
    );
  }

  if (state === 'connecting') {
    return (
      <div className="page room-page">
        <div className="room-center">
          <div className="room-spinner" />
          <p className="room-text">Joining meeting...</p>
        </div>
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="page room-page">
        <div className="room-center room-card">
          <h2 className="room-title">Meeting</h2>
          <EndCallControl key={meetingId} canEnd={canEnd} status={meeting?.status} onEnd={meetingEnd} />
          {failure === 'mic-denied' ? (
            <>
              <p className="room-text">Your browser refused the microphone. Allow it (the icon in the address bar) and try again.</p>
              <button type="button" className="link-btn" onClick={() => void connectToRoom()}>
                Try again
              </button>
            </>
          ) : failure === 'ended' ? (
            <>
              <p className="room-text">{leftCall && !ended ? 'You have left the call.' : 'This meeting has ended.'}</p>
              <button type="button" className="link-btn" onClick={() => navigate(meeting ? `/p/${meeting.projectId}` : '/')}>
                Back to the project
              </button>
            </>
          ) : failure === 'stale' ? (
            <>
              <p className="room-text">Jabberr was updated since this tab was opened. Reload the page and join again.</p>
              <button type="button" className="link-btn" onClick={() => window.location.reload()}>
                Reload
              </button>
            </>
          ) : (
            <>
              <p className="room-text">The meeting room could not be reached. The transcript still works.</p>
              <button type="button" className="link-btn" onClick={() => void connectToRoom()}>
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Connected state: the full room UI.
  const botStatus = status;
  const peopleList = Object.values(participants).filter((p) => p.identity !== BOT_IDENTITY && p.identity !== (user?.id || ''));
  const selfKey = user?.name || user?.id || '';
  const selfInfo = participants[selfKey] ? { ...participants[selfKey], isSelf: true } : null;
  const isSpeaking = (p: { identity: string; name?: string }): boolean => speakingIds.has(p.identity) || (!!p.name && speakingIds.has(p.name));

  const callSettings = (
    <CallSettings badge={professionalActive}>
      {meetingId && listenMode !== null && (
        <SettingsSection title="Listening mode">
          <TalkModeControl
            value={listenMode}
            busy={listenBusy}
            onChange={(mode) => {
              setListenBusy(true);
              meetingUpdateListenMode(mode)
                .then(() => setListenMode(mode))
                .catch(error => setParticipationMessage(String(error)))
                .finally(() => setListenBusy(false));
            }}
          />
          <span className="room-hint">{talkModeHint(listenMode)}</span>
        </SettingsSection>
      )}
      <SettingsSection title="Microphone">
        <CaptureProfileControl value={captureProfile} busy={captureBusy} onChange={value => void changeCaptureProfile(value)} />
        {captureError && <p role="alert">{captureError}</p>}
        {effectiveCapture && <p className="room-hint">{effectiveCapture}</p>}
      </SettingsSection>
      {meetingId && user && (
        <SettingsSection title="Professional">
          <ProfessionalControl listenOnly={listenMode === 'silent'} meetingId={meetingId} userId={user.id} connected={state === 'connected' && !ended} readOnly />
        </SettingsSection>
      )}
      <SettingsSection title="Captions">
        <label className="room-call-settings-check">
          <input type="checkbox" checked={showTranscript} onChange={() => setShowTranscript(v => !v)} /> Show captions and transcript
        </label>
      </SettingsSection>
      {canEnd && (
        <SettingsSection title="Appearance">
          <MeetingAppearance appearance={appearance} />
        </SettingsSection>
      )}
      <SettingsSection title="Jabberr and connection">
        {jabberrInRoom && <button type="button" className="btn btn-secondary" disabled={!controlsReady || participationBusy} onClick={() => void signalParticipation('stop')}>Stop speaking</button>}
        <button type="button" className="btn btn-secondary" onClick={() => void reconnect()}>Reconnect</button>
        {canEnd && <button type="button" className="btn btn-secondary" onClick={() => void leave()}>Leave without ending</button>}
      </SettingsSection>
    </CallSettings>
  );

  // Render the header for both live and ended states
  const renderHeader = () => {
    if (!meeting) return null;
    return (
      <MeetingHeader key={meetingId}
        meeting={{ title: meeting.title, startedAt: meeting.startedAt, status: meeting.status, recordingId: meeting.recordingId }}
        project={project}
        onRename={(title) => void updateTitle(title)}
        avatarId={appearance.avatarId}
        appearance={callSettings}
      />
    );
  };

  return (
    <div className="page room-page room-page-live">
      {renderHeader()}
      {endError && <p role="alert">{endError}</p>}
      {confirmEndCall === meetingId && <ConfirmDialog title="End this call?" body="This ends the call for everyone and saves its transcript." confirmLabel="End call" onCancel={() => setConfirmEndCall(null)} onConfirm={() => { setConfirmEndCall(null); void handleEnd(); }} />}
      {assistantJoinError && <p role="alert">{assistantJoinError}</p>}
      {confirmLeave === meetingId && <ConfirmDialog title="End the meeting before leaving?" body={<><p>You are the last person here. Ending saves the transcript for everyone.</p><button type="button" className="link-btn" onClick={() => { setConfirmLeave(null); void leaveRoom(); }}>Leave without ending</button></>} confirmLabel="End meeting" onCancel={() => setConfirmLeave(null)} onConfirm={() => { setConfirmLeave(null); void handleEnd(true); }} />}

      <div className={`room-container ${showPeople || showTranscript ? 'room-with-side-panels' : ''}`}>
        <div className="room-with-panel">
          <div className="room-stage">
            {/* Audio blocked banner */}
            {audioBlocked && (
              <div className="room-audio-banner" role="alert">
                <span>Your browser blocked the room&apos;s sound.</span>
                <button type="button" className="btn btn-primary" onClick={handleStartAudio}>
                  Turn sound on
                </button>
              </div>
            )}

            {/* Speaking grid stage */}
            <div className="room-speaking-grid" aria-label="People in room">
              {Object.keys(participants).length === 0 ? (
                <p className="room-empty">Waiting for others to join...</p>
              ) : (
                <>
                  {/* Self first, then Jabberr, then everyone else: the speaker's block lights up. */}
                  {selfInfo && (
                    <PersonTile
                      key={selfInfo.identity}
                      name={`${selfInfo.name || 'You'} (you)`}
                      avatarName={selfInfo.name || 'You'}
                      avatarId={selfInfo.name || selfInfo.identity}
                      speaking={!muted && isSpeaking(selfInfo)}
                      muted={muted}
                    />
                  )}
                  {Object.values(participants).filter((p) => p.identity === BOT_IDENTITY).map((p) => (
                    <JabberrTile key={p.identity} status={botStatus} duplex={liveDuplex} listenMode={listenMode} avatarId={appearance.avatarId} />
                  ))}
                  {peopleList.map((p) => (
                    <PersonTile
                      key={p.identity}
                      name={p.name || p.identity}
                      avatarName={p.name || p.identity}
                      avatarId={p.name || p.identity}
                      speaking={isSpeaking(p)}
                      muted={p.muted === true}
                    />
                  ))}
                </>
              )}
            </div>

          </div>

          {/* Transcript side panel - reads from meeting segments, not chat */}
          <div className={`room-transcript-panel ${showTranscript ? 'room-transcript-panel-open' : ''}`}>
            <div className="room-transcript-panel-header">
              <h3>Transcript</h3>
              <button
                type="button"
                className="room-panel-close"
                onClick={() => setShowTranscript(false)}
                aria-label="Close transcript"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div
              className="room-transcript-panel-body"
              ref={transcriptRef}
              onScroll={handleTranscriptScroll}
              aria-label="Meeting transcript"
              role="log"
              aria-live="polite"
            >
              {segments.length === 0 ? (
                <p className="room-empty">Nothing said yet. Jabberr will show what he hears here.</p>
              ) : (
                <TranscriptGroups segments={segments}>{(seg, i) => {
                  const avatarId = seg.speaker === 'Jabberr' ? BOT_IDENTITY : seg.speaker;
                  return (
                    <div key={i} className="room-turn room-turn-text">
                      <Avatar name={seg.speaker} id={avatarId} size={32} bot={seg.speaker === 'Jabberr'} avatarId={appearance.avatarId} />
                      <div className="room-turn-content">
                        <div className="room-turn-header">
                          <span className="room-turn-name">{seg.speaker}</span>
                          <span className="room-turn-time" aria-label={`at ${formatSegmentTime(seg.start)}`}>
                            {formatSegmentTime(seg.start)}
                          </span>
                        </div>
                        {seg.responseId && seg.pieceIndex === 0 && !ended && (
                          <div className="room-participation-feedback" aria-label="Rate Jabberr’s contribution">
                            {rated[seg.responseId] ? <span>Feedback saved</span> : <>
                              <button onClick={() => void rateContribution(seg.responseId!, 'useful')}>Useful</button>
                              <button onClick={() => void rateContribution(seg.responseId!, 'not_needed')}>Not needed</button>
                              <button onClick={() => void rateContribution(seg.responseId!, 'bad_timing')}>Wrong moment</button>
                            </>}
                          </div>
                        )}
                        <div className="room-turn-text">
                          {seg.speaker === 'Jabberr' ? <Markdown text={seg.text} /> : <span>{seg.text}</span>}
                        </div>
                      </div>
                    </div>
                  );
                }}</TranscriptGroups>
              )}
              {showNewPill && (
                <button type="button" className="room-new-pill" onClick={jumpToBottom} aria-label="Jump to new messages">
                  New messages
                </button>
              )}
            </div>
          </div>

          {/* RoomPanel: people list and Jabberr invite row */}
          {showPeople && user && (
            <RoomPanel
              me={{ id: user.id, name: user.name, email: user.email, status: selfPresence, inRoom: true }}
              users={panelUsers}
              inRoomIds={[...Object.values(participants).map((p) => p.identity), user.id]}
              onInvite={handleInvite}
              onStatusChange={handleStatusChange}
              state={state}
              meetingId={meetingId || ''}
              jabberrInRoom={jabberrInRoom}
            />
          )}
        </div>

        {/* Jabberr status bar above control bar */}
        <div className={`room-status-bar tone-${!liveDuplex && botStatus && jabberrInRoom ? botStatus.tone : 'idle'}`} aria-label="Jabberr status">
          {liveDuplex ? <DuplexStatus signals={liveDuplex} /> : jabberrInRoom ? (
            botStatus ? (
              <>
                <StatusIndicator state={botStatus.icon} />
                <span className="room-status-text" aria-live="polite">
                  <strong>{BOT_NAME}</strong> · {botStatus.text}
                  {botStatus.mode === 'pulsing-dots' && (
                    <span className="pulsing-dots" aria-hidden="true">
                      <span className="dot"></span><span className="dot"></span><span className="dot"></span>
                    </span>
                  )}
                  {botStatus.mode === 'level-bars' && (
                    <span className="level-bars" aria-hidden="true">
                      <span className="bar bar-1"></span>
                      <span className="bar bar-2"></span>
                      <span className="bar bar-3"></span>
                    </span>
                  )}
                  {botStatus.extra && (
                    <span className="room-heard-text">{botStatus.extra}</span>
                  )}
                </span>
              </>
            ) : (
              <span className="room-status-text" aria-live="polite">Jabberr is in the room</span>
            )
          ) : (
            <span className="room-status-text" aria-live="polite">
              Jabberr is not in this room
              <button
                type="button"
                className="room-status-invite"
                onClick={() => void api.meetings.inviteJabberr(meetingId || '').catch(() => {})}
                aria-label="Invite Jabberr"
              >
                Invite
              </button>
            </span>
          )}
        </div>

        {/* Control bar */}
        <div className="room-controls" aria-label="Room controls">
          <div className="room-control-bar">
            {meetingId && user && <ProfessionalControl listenOnly={listenMode === 'silent'} meetingId={meetingId} userId={user.id} connected={state === 'connected' && !ended} onActiveChange={setProfessionalActive} />}
            <button
              type="button"
              className="room-btn room-btn-mute"
              onClick={toggleMute}
              aria-pressed={muted}
              aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M16 10a4 4 0 0 1-8 0"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              )}
            </button>
            <button
              type="button"
              className="room-btn room-btn-transcript"
              onClick={() => setShowTranscript((v) => !v)}
              aria-pressed={showTranscript}
              aria-label="Toggle transcript"
              title="Transcript"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </button>
            <button
              type="button"
              className="room-btn room-btn-mute"
              onClick={() => setShowPeople((v) => !v)}
              aria-pressed={showPeople}
              aria-label="Toggle people panel"
              title="People"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
            </button>
        {jabberrInRoom && !ended && (
          <details className="room-participation" aria-label="Guide Jabberr"><summary aria-label="Guide Jabberr" title="Guide Jabberr"><span className="room-guide-label">Guide Jabberr</span><span className="room-guide-trigger-icon"><MoreIcon /></span></summary><div className="room-guide-menu">
          {meetingId && listenMode !== null && (
            <div className="room-listen-control" aria-label="When Jabberr talks">
              <TalkModeControl
                value={listenMode}
                busy={listenBusy}
                onChange={(mode) => {
                  setListenBusy(true);
                  meetingUpdateListenMode(mode)
                    .then(() => setListenMode(mode))
                    .catch(error => setParticipationMessage(String(error)))
                    .finally(() => setListenBusy(false));
                }}
              />
              <span className="room-hint">{talkModeHint(listenMode)}{peers.includes(BOT_IDENTITY) ? '' : ' Jabberr is not in the room yet.'}</span>
            </div>
          )}

            <button className="btn btn-secondary" disabled={listenMode === 'silent' || !controlsReady || participationBusy || !canInviteFromSegments(segments, canAnswerSharedInput)} onClick={() => void signalParticipation('answer')}>Your turn, Jabberr</button>
            <button className="btn btn-secondary" disabled={!user?.id || participationBusy || !segments.some(isHumanMeetingSegment)} onClick={() => {
              const index = segments.map(isHumanMeetingSegment).lastIndexOf(true);
              if (!user?.id || !meetingId || index < 0) return;
              setParticipationBusy(true);
              void signalSubmissionId(user.id, meetingId, `missed:${index}`)
                .then(submissionId => submitFeedback(user.id, meetingId, { kind: 'missed_intervention', submissionId, observedAt: new Date().toISOString(), segmentIndex: index, comment: '' }, `missed:${index}`))
                .then(saved => setParticipationMessage(saved ? 'Missed intervention saved to the corpus.' : 'Feedback saved on this device for retry.'))
                .catch(error => setParticipationMessage(String(error))).finally(() => setParticipationBusy(false));
            }}>Jabberr should have talked</button>
            <button className="btn btn-secondary" disabled={!controlsReady || participationBusy} onClick={() => void signalParticipation('stop')}>Stop this reply</button>
            <span role="status">{participationMessage || (controlsReady ? 'Invite an answer or give the conversation back to the room.' : 'Participation controls are reconnecting.')}</span>
          </div></details>
        )}
            <button
              type="button"
              className="room-btn room-btn-leave"
              onClick={() => canEnd ? setConfirmEndCall(meetingId || null) : void leave()}
              aria-label={canEnd ? "End call" : "Leave room"}
              title={canEnd ? "End call" : "Leave"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>

        </div>
      </div>

      {/* Audio host: hidden div for LiveKit audio elements. */}
      <div ref={audioHostRef} className="room-audio-host" aria-hidden="true" />

      {/* Invite cards (fixed, outside room container) */}
      {invites.map((invite) => (
        <div key={invite.meetingId} className="room-invite-card">
          <span className="room-invite-card-text">{invite.invitedBy} invited you to {invite.title}</span>
          <div className="room-invite-card-actions">
            <button type="button" className="room-invite-card-join" onClick={() => joinInvite(invite)}>
              Join
            </button>
            <button type="button" className="room-invite-card-dismiss" onClick={() => dismissInvite(invite)}>
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
